import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment,
  List, ListItem, ListItemButton, ListItemText, ListItemAvatar,
  Avatar, Badge, Divider, Button, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Chip, useTheme, useMediaQuery, Popover
} from '@mui/material';
import {
  Search, Add, Send, AttachFile, ArrowBack, Chat,
  Person, Close, CheckCircle, DeleteOutline, Mic, Stop, AccessTime,
  Reply, InsertEmoticon, WhatsApp, AutoAwesome
} from '@mui/icons-material';
import { INTRO_TEMPLATES, isThinOpener, missingIntroFacts } from '../utils/contractorIntro';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import messagingService from '../services/messagingService';
import apiClient from '../services/api';
import notificationService from '../services/notificationService';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand, border } from '../theme/colors';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d`;
  return d.toLocaleDateString();
}

function fullTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Local YYYY-MM-DD key so we can tell when two messages fall on different days.
function dayKey(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// Label for the date divider between days: Today / Yesterday / full date.
function formatDateDivider(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(dateStr) === dayKey(today)) return 'Today';
  if (dayKey(dateStr) === dayKey(yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

function initials(firstName, lastName) {
  return `${(firstName || '?')[0]}${(lastName || '')[0] || ''}`.toUpperCase();
}

// Businesses appear under their trading name; sole traders under their own.
function contactName(firstName, lastName, companyName) {
  return (companyName || '').trim() || `${firstName || ''} ${lastName || ''}`.trim() || 'User';
}

// Initials from whatever name is displayed ("Trending Solutions" → TS).
function initialsFrom(name) {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return `${words[0][0]}${words[1]?.[0] || ''}`.toUpperCase();
}

// ─── NewMessageDialog ─────────────────────────────────────────────────────────

function NewMessageDialog({ open, onClose, onConversationCreated }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setError('');
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await messagingService.searchUsers(query.trim());
        setResults(res.data?.users || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [query]);

  const handleSelect = async (u) => {
    setCreating(true);
    setError('');
    try {
      const res = await messagingService.getOrCreateConversation(u.id);
      onConversationCreated(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to start conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogCloseButton onClick={onClose} />
      <DialogTitle>
        New Message
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder="Search by name…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={16} /> : <Search fontSize="small" />}
              </InputAdornment>
            )
          }}
          sx={{ mb: 1 }}
        />
        <List disablePadding dense>
          {results.map(u => {
            const display = contactName(u.first_name, u.last_name, u.company_name);
            const person = `${u.first_name || ''} ${u.last_name || ''}`.trim();
            return (
              <ListItem key={u.id} disablePadding>
                <ListItemButton onClick={() => handleSelect(u)} disabled={creating}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: brand.primary, fontSize: 14 }}>
                      {initialsFrom(display)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={display}
                    secondary={display === person ? u.role : `${person} · ${u.role}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          {!searching && query.trim() && results.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No users found
            </Typography>
          )}
        </List>
      </DialogContent>
    </Dialog>
  );
}

// ─── WhatsAppBanner ───────────────────────────────────────────────────────────
// "Take your chats to WhatsApp" call-to-action. Renders nothing until the
// backend reports the WhatsApp line is live (WHATSAPP_PUBLIC_NUMBER set).
// Dismissible; remembers the dismissal per browser.

function WhatsAppBanner() {
  const [wa, setWa] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem('eqaya:wa-banner-dismissed') === '1');

  useEffect(() => {
    let cancelled = false;
    messagingService.getWhatsAppInfo?.()
      .then((info) => { if (!cancelled && info?.enabled) setWa(info); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!wa || hidden) return null;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
      bgcolor: 'rgba(37, 211, 102, 0.08)', borderBottom: 1, borderColor: 'divider'
    }}>
      <WhatsApp sx={{ color: '#25D366', fontSize: 20 }} />
      <Typography variant="caption" sx={{ flex: 1, lineHeight: 1.3 }}>
        Get these chats on WhatsApp — message and reply without logging in.
      </Typography>
      <Button
        size="small" variant="contained" disableElevation
        href={wa.waLink} target="_blank" rel="noopener"
        sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' }, textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        Connect
      </Button>
      <IconButton size="small" onClick={() => { localStorage.setItem('eqaya:wa-banner-dismissed', '1'); setHidden(true); }}>
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ─── ConversationList ─────────────────────────────────────────────────────────

function ConversationList({ conversations, selected, onSelect, onNew, onDelete, loading }) {
  const [search, setSearch] = useState('');

  const filtered = conversations.filter(c => {
    const name = `${c.other_company_name || ''} ${c.other_first_name || ''} ${c.other_last_name || ''}`.toLowerCase();
    const proj = (c.project_title || '').toLowerCase();
    const last = (c.last_message || '').toLowerCase();
    const q = search.toLowerCase();
    return !q || name.includes(q) || proj.includes(q) || last.includes(q);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>Messages</Typography>
          <Tooltip title="New Message">
            <IconButton size="small" color="primary" onClick={onNew}>
              <Add />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth size="small" placeholder="Search conversations…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
          }}
        />
      </Box>

      <WhatsAppBanner />

      {/* List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 6, px: 2 }}>
            <Chat sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" variant="body2">
              {conversations.length === 0 ? 'No conversations yet' : 'No results'}
            </Typography>
            {conversations.length === 0 && (
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={onNew} sx={{ mt: 2 }}>
                Start one
              </Button>
            )}
          </Box>
        ) : (
          <List disablePadding>
            {filtered.map(c => {
              const name = contactName(c.other_first_name, c.other_last_name, c.other_company_name);
              const unread = parseInt(c.unread_count, 10) || 0;
              const isSelected = selected?.id === c.id;
              return (
                <React.Fragment key={c.id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <Tooltip title="Remove conversation">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                          sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '.MuiListItem-root:hover &': { opacity: 0.6 },
                            '&:hover': { opacity: '1 !important', color: 'error.main' }
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => onSelect(c)}
                      sx={{
                        px: 2, py: 1.5,
                        '&.Mui-selected': {
                          bgcolor: 'rgba(0, 188, 212, 0.1)',
                          borderRight: '3px solid #00bcd4'
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge badgeContent={unread} color="error" overlap="circular">
                          <Avatar
                            src={c.other_avatar}
                            sx={{ width: 44, height: 44, bgcolor: brand.primary, fontSize: 16 }}
                          >
                            {initialsFrom(name)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                              variant="body2"
                              fontWeight={unread > 0 ? 700 : 400}
                              noWrap
                              sx={{ maxWidth: 140 }}
                            >
                              {name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(c.last_message_at || c.updated_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {c.project_title && (
                              <Typography variant="caption" color="primary.main" display="block" noWrap>
                                {c.project_title}
                              </Typography>
                            )}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              fontWeight={unread > 0 ? 600 : 400}
                            >
                              {c.last_message || 'Start the conversation'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}

// ─── MessageThread ────────────────────────────────────────────────────────────

// Curated palette: everyday reactions first, then construction-flavoured ones.
const EMOJI_PALETTE = [
  '😀', '😂', '🤣', '😊', '😉', '😍', '🤝', '👍', '👎', '👌', '🙏', '👏',
  '💪', '🙌', '✌️', '❤️', '🔥', '⭐', '✅', '❌', '⚠️', '🎉', '😅', '🤔',
  '😢', '😭', '😡', '🙄', '😎', '🥰', '🏠', '🏗️', '🧱', '🔨', '🛠️', '🔧',
  '📐', '📏', '🚧', '🪜', '🪚', '⚡', '💧', '🎨', '💰', '📷', '📎', '🕐'
];

function MessageThread({ conversation, currentUserId, onBack, showBack }) {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [quoteNudgeDismissed, setQuoteNudgeDismissed] = useState(true);
  const [dealActionBusy, setDealActionBusy] = useState(false);
  const [dealRequestSent, setDealRequestSent] = useState(false);
  useEffect(() => { setDealRequestSent(false); }, [conversation?.id]);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);  // now stores the actual File object
  const [uploadProgress, setUploadProgress] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [replyTo, setReplyTo] = useState(null);        // message being quoted
  const [highlightId, setHighlightId] = useState(null); // flash after jump-to-quoted
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [thinOpenerWarning, setThinOpenerWarning] = useState(false);
  useEffect(() => { setShowTemplates(false); setThinOpenerWarning(false); }, [conversation?.id]);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const earliestPageRef = useRef(1);   // deepest history page fetched so far
  const newestIdRef = useRef(null);    // last message id we auto-scrolled for
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const messageRefs = useRef({});   // message id -> DOM node, for jump-to-quoted
  const inputElRef = useRef(null);  // raw <textarea>, for caret-aware emoji insert

  const startReply = (message) => {
    setReplyTo(message);
    inputElRef.current?.focus();
  };

  const scrollToMessage = (id) => {
    const el = messageRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightId(id);
    window.setTimeout(() => setHighlightId(null), 1600);
  };

  const insertEmoji = (emoji) => {
    const el = inputElRef.current;
    const start = el?.selectionStart ?? input.length;
    const end = el?.selectionEnd ?? input.length;
    setInput((prev) => prev.slice(0, start) + emoji + prev.slice(end));
    window.requestAnimationFrame(() => {
      if (el) {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      }
    });
  };

  // Slack-style "It's 6:17 AM for {name}": shown when the other party's
  // captured timezone differs from the viewer's. Ticks every 30s.
  const [clockNow, setClockNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClockNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const otherLocalTime = (() => {
    const tz = conversation?.other_timezone;
    if (!tz) return null;
    try {
      const mine = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (mine === tz) return null;
      return clockNow.toLocaleTimeString('en-US', {
        timeZone: tz, hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return null;
    }
  })();

  // Union by id, chronological. Polls fetch only the newest page, so they
  // must MERGE into what's loaded — replacing state would throw away any
  // earlier pages the user pulled in via "Load earlier messages".
  const mergeMessages = (prev, incoming) => {
    const byId = new Map(prev.map((m) => [m.id, m]));
    incoming.forEach((m) => byId.set(m.id, m));
    return [...byId.values()].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at) || a.id - b.id
    );
  };

  const load = useCallback(async () => {
    if (!conversation?.id) return;
    try {
      const res = await messagingService.getMessages(conversation.id);
      if (res.success) {
        setMessages((prev) => mergeMessages(prev, res.data.messages || []));
        if (earliestPageRef.current === 1) {
          setHasEarlier(Boolean(res.data.pagination?.has_more));
        }
      }
    } catch {
      // silent polling failure is ok
    }
  }, [conversation?.id]);

  const loadEarlier = async () => {
    if (!conversation?.id || loadingEarlier) return;
    setLoadingEarlier(true);
    try {
      const nextPage = earliestPageRef.current + 1;
      const res = await messagingService.getMessages(conversation.id, nextPage);
      if (res.success) {
        earliestPageRef.current = nextPage;
        setMessages((prev) => mergeMessages(prev, res.data.messages || []));
        setHasEarlier(Boolean(res.data.pagination?.has_more));
      }
    } catch {
      // leave the button available to retry
    } finally {
      setLoadingEarlier(false);
    }
  };

  // Load on mount and when conversation changes; subscribe to socket for real-time updates
  useEffect(() => {
    if (!conversation?.id) return;
    setMessages([]);
    setReplyTo(null);
    setHasEarlier(false);
    setQuoteNudgeDismissed(!!localStorage.getItem(`eqaya_quote_nudge_${conversation.id}`));
    earliestPageRef.current = 1;
    newestIdRef.current = null;
    setLoading(true);
    // Fetching messages marks the thread read server-side, so tell the
    // Layout badges to re-count right away instead of on the next poll.
    load().finally(() => {
      setLoading(false);
      window.dispatchEvent(new Event('eqaya:unread-refresh'));
    });

    // Fast path: NotificationService.createNotification emits a 'notification'
    // socket event to the recipient's user room for every new message.
    const unsubNotification = notificationService.on('notification', () => {
      load().then(() => window.dispatchEvent(new Event('eqaya:unread-refresh')));
    });

    // Fallback: poll while the conversation is open, in case the socket is
    // down or the room join was refused (e.g. expired session).
    pollRef.current = setInterval(load, 15000);

    return () => {
      unsubNotification();
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [conversation?.id, load]);

  // Scroll to bottom only when a NEW newest message arrives — loading
  // earlier history prepends above and must not yank the view down.
  useEffect(() => {
    const newestId = messages.length ? messages[messages.length - 1].id : null;
    if (newestId !== newestIdRef.current) {
      newestIdRef.current = newestId;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Contractor writing to a client, before they have sent anything in this
  // thread: the moment a first impression is made.
  const otherIsClient = String(conversation?.other_user_role || '').toLowerCase().includes('client');
  const contractorFirstContact = authUser?.role === 'contractor' && otherIsClient
    && !messages.some((m) => m.sender_id === currentUserId);
  const clientDisplayName = [conversation?.other_first_name, conversation?.other_last_name].filter(Boolean).join(' ');

  const applyTemplate = (tpl) => {
    setInput(tpl.build({ user: authUser, clientName: clientDisplayName, projectTitle: conversation?.project_title }));
    setShowTemplates(false);
    setThinOpenerWarning(false);
    setTimeout(() => inputElRef.current?.focus(), 0);
  };

  const handleSend = async ({ force = false } = {}) => {
    const text = input.trim();
    if (!text && !attachment) return;
    if (!force && contractorFirstContact && !attachment && isThinOpener(text)) {
      // A bare "Hi" or a lone phone number rarely gets a reply. Ask once.
      setThinOpenerWarning(true);
      return;
    }
    setThinOpenerWarning(false);
    setSending(true);
    setUploadProgress(0);
    setError('');
    try {
      const onProgress = attachment ? (pct) => setUploadProgress(pct) : null;
      const res = await messagingService.sendMessage(conversation.id, text, attachment, onProgress, replyTo?.id || null);
      if (res.success) {
        setInput('');
        setAttachment(null);
        setReplyTo(null);
        setUploadProgress(0);
        await load();
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
      setUploadProgress(0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Per-category size limits (matches backend uploadMiddleware)
    const mime = file.type || '';
    let maxMB = 10;
    if (mime.startsWith('video/'))      maxMB = 100;
    else if (mime.startsWith('audio/')) maxMB = 25;
    else if (mime.startsWith('image/')) maxMB = 10;
    else                                maxMB = 25; // documents
    if (file.size > maxMB * 1024 * 1024) {
      setError(`File size exceeds ${maxMB}MB limit for ${mime.startsWith('video/') ? 'videos' : mime.startsWith('audio/') ? 'audio' : mime.startsWith('image/') ? 'images' : 'documents'}`);
      e.target.value = '';
      return;
    }
    setAttachment(file);  // store the actual File object for upload
    setError('');
    e.target.value = '';
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(recordingTimerRef.current);
        setRecordingTime(0);
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        setAttachment(file);
        setRecording(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      setError('Microphone access denied. Please allow microphone access to send voice notes.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.ondataavailable = () => {}; // discard chunks
      mediaRecorderRef.current.onstop = () => {
        clearInterval(recordingTimerRef.current);
        setRecordingTime(0);
        setRecording(false);
      };
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
    }
  };

  const formatRecTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (!conversation) {
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100%', gap: 2
      }}>
        <Chat sx={{ fontSize: 64, color: 'text.disabled' }} />
        <Typography variant="h6" color="text.secondary">
          Select a conversation to start messaging
        </Typography>
      </Box>
    );
  }

  const otherName = contactName(conversation.other_first_name, conversation.other_last_name, conversation.other_company_name);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Thread header */}
      <Box sx={{
        px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fafafa'
      }}>
        {showBack && (
          <IconButton size="small" onClick={onBack}>
            <ArrowBack />
          </IconButton>
        )}
        <Avatar
          src={conversation.other_avatar}
          sx={{ width: 40, height: 40, bgcolor: brand.primary, fontSize: 15 }}
        >
          {initialsFrom(otherName)}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>{otherName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {conversation.other_user_role}
            {conversation.project_title ? ` · ${conversation.project_title}` : ''}
          </Typography>
        </Box>
      </Box>

      {/* Standing "agreed a price?" shortcuts — bring offline deals back as an
          official quotation (client asks / contractor sends). Every
          client↔contractor thread, no detection needed. */}
      {(() => {
        const myRole = authUser?.role;
        const otherRole = String(conversation.other_user_role || '').toLowerCase();
        const clientContractorThread =
          (myRole === 'contractor' && otherRole.includes('client')) ||
          (myRole === 'client' && otherRole.includes('contractor'));
        if (!clientContractorThread) return null;
        const requestQuote = async () => {
          setDealActionBusy(true);
          try {
            await apiClient.post(`/chat-deal/${conversation.id}/request-quote`);
            setDealRequestSent(true);
          } catch (e) {
            setError(e.response?.data?.error || 'Failed to send the request');
          } finally {
            setDealActionBusy(false);
          }
        };
        const sendQuote = async () => {
          setDealActionBusy(true);
          try {
            const r = await apiClient.get(`/chat-deal/${conversation.id}/quote-target`);
            const pid = r.data?.data?.projectId;
            navigate(pid ? `/contractor/marketplace?project=${pid}` : '/contractor/marketplace');
          } catch (_e) {
            navigate('/contractor/marketplace');
          } finally {
            setDealActionBusy(false);
          }
        };
        return (
          <Box sx={{
            mx: 2, mt: 1, px: 1.5, py: 0.75, borderRadius: 2,
            bgcolor: 'rgba(67,181,129,0.06)', border: '1px solid rgba(67,181,129,0.25)',
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Agreed a price{myRole === 'client' ? '?' : ' with this client?'}
            </Typography>
            {myRole === 'client' ? (
              <Button size="small" variant="outlined" color="success" disabled={dealActionBusy || dealRequestSent} onClick={requestQuote}>
                {dealRequestSent ? 'Quotation requested ✓' : 'Request official quotation'}
              </Button>
            ) : (
              <Button size="small" variant="outlined" color="success" disabled={dealActionBusy} onClick={sendQuote}>
                Send official quotation
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              {myRole === 'client'
                ? 'Accept it, fund your wallet — never pay cash upfront.'
                : 'Their acceptance links the funded wallet to you — no deposit needed.'}
            </Typography>
          </Box>
        );
      })()}

      {/* Soft nudge when quote-looking documents are shared as plain attachments.
          Never blocks anything — official quotations are the protected path. */}
      {(() => {
        const myRole = authUser?.role;
        const otherRole = String(conversation.other_user_role || '').toLowerCase();
        const clientContractorThread =
          (myRole === 'contractor' && otherRole.includes('client')) ||
          (myRole === 'client' && otherRole.includes('contractor'));
        const hasDocAttachment = messages.some(
          (m) => m.attachment_name && !['image', 'video', 'voice'].includes(m.message_type)
        );
        if (!clientContractorThread || !hasDocAttachment || quoteNudgeDismissed) return null;
        return (
          <Alert
            severity="info"
            sx={{ mx: 2, mt: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
            onClose={() => {
              localStorage.setItem(`eqaya_quote_nudge_${conversation.id}`, '1');
              setQuoteNudgeDismissed(true);
            }}
          >
            {myRole === 'contractor'
              ? 'Sending a quotation? Attachments alone aren’t eligible for Eqaya escrow or dispute support. Open the project and choose "Upload Your Own Quotation" — your client gets your document plus a secure Accept & Pay button.'
              : 'Received a quotation as an attachment? Payments for it aren’t protected by Eqaya escrow, acceptance record, or dispute support. Ask the contractor to send it as an official quotation — it takes them two taps.'}
          </Alert>
        );
      })()}

      {/* Messages area */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
              No messages yet. Start the conversation!
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
              {[
                'Hi! I\'d like to discuss a project with you.',
                'Are you available for a new project?',
                'Can you share your rates for this type of work?',
                'I saw your profile and I\'m interested in working together.',
              ].map((template, i) => (
                <Box
                  key={i}
                  onClick={() => setInput(template)}
                  sx={{
                    px: 2, py: 1, borderRadius: 3,
                    border: '1px solid', borderColor: 'divider',
                    cursor: 'pointer', fontSize: '0.8rem', color: 'text.secondary',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  }}
                >
                  {template}
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <>
          {hasEarlier && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Chip
                label={loadingEarlier ? 'Loading…' : 'Load earlier messages'}
                size="small"
                clickable={!loadingEarlier}
                onClick={loadEarlier}
                sx={{ fontSize: '0.75rem' }}
              />
            </Box>
          )}
          {messages.map((m, idx) => {
            const isMine = m.sender_id === currentUserId;
            const prev = idx > 0 ? messages[idx - 1] : null;
            const showDateDivider = !prev || dayKey(prev.created_at) !== dayKey(m.created_at);
            return (
              <React.Fragment key={m.id}>
              {showDateDivider && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      px: 1.5, py: 0.25, borderRadius: 999,
                      bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary',
                      fontWeight: 600, letterSpacing: 0.2,
                    }}
                  >
                    {formatDateDivider(m.created_at)}
                  </Typography>
                </Box>
              )}
              <Box
                ref={(el) => { messageRefs.current[m.id] = el; }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isMine ? 'flex-end' : 'flex-start',
                  mb: 0.5,
                  '&:hover .msg-reply-btn': { opacity: 1 }
                }}
              >
                {!isMine && (
                  <Avatar
                    src={m.sender_avatar}
                    sx={{ width: 28, height: 28, mr: 1, mt: 0.5, bgcolor: brand.primary, fontSize: 12, alignSelf: 'flex-start' }}
                  >
                    {initials(m.sender_first_name, m.sender_last_name)}
                  </Avatar>
                )}
                {isMine && (
                  <Tooltip title="Reply">
                    <IconButton
                      className="msg-reply-btn"
                      size="small"
                      onClick={() => startReply(m)}
                      sx={{ mr: 0.5, opacity: { xs: 0.35, md: 0 }, transition: 'opacity 0.15s', color: 'text.secondary' }}
                    >
                      <Reply sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Box sx={{ maxWidth: '70%' }}>
                  <Box
                    sx={{
                      px: 1.5, py: 1,
                      borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      bgcolor: isMine ? '#00bcd4' : '#f0f0f0',
                      color: isMine ? 'white' : 'text.primary',
                      boxShadow: highlightId === m.id ? '0 0 0 3px rgba(0,188,212,0.5)' : 'none',
                      transition: 'box-shadow 0.3s'
                    }}
                  >
                    {m.reply_to_message_id && (
                      <Box
                        onClick={() => scrollToMessage(m.reply_to_message_id)}
                        sx={{
                          mb: 0.5, px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                          borderLeft: '3px solid',
                          borderColor: isMine ? 'rgba(255,255,255,0.75)' : '#00bcd4',
                          bgcolor: isMine ? 'rgba(255,255,255,0.18)' : 'rgba(0,188,212,0.08)'
                        }}
                      >
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: isMine ? 'rgba(255,255,255,0.95)' : '#0097a7' }}>
                          {m.reply_to_sender_id === currentUserId ? 'You' : (m.reply_to_sender_display_name || 'Message')}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260, opacity: 0.9 }}>
                          {m.reply_to_content
                            ? m.reply_to_content
                            : (m.reply_to_message_type && m.reply_to_message_type !== 'text' ? '📎 Attachment' : 'Message unavailable')}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                      {m.content}
                    </Typography>
                    {m.attachment_url && m.message_type === 'image' && (
                      <Box
                        component="a"
                        href={m.attachment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        <Box
                          component="img"
                          src={m.attachment_url}
                          alt={m.attachment_name || 'image'}
                          sx={{ maxWidth: 240, maxHeight: 180, borderRadius: 1, objectFit: 'cover', cursor: 'pointer' }}
                        />
                      </Box>
                    )}
                    {m.attachment_url && m.message_type === 'video' && (
                      <Box sx={{ mt: 0.5, maxWidth: 300 }}>
                        <Box
                          component="video"
                          src={m.attachment_url}
                          controls
                          preload="metadata"
                          sx={{
                            width: '100%',
                            maxHeight: 220,
                            borderRadius: 1,
                            bgcolor: '#000'
                          }}
                        />
                      </Box>
                    )}
                    {m.attachment_url && m.message_type === 'voice' && (
                      <Box sx={{ mt: 0.5, width: '100%', minWidth: 200, maxWidth: 280 }}>
                        <Box
                          component="audio"
                          src={m.attachment_url}
                          controls
                          preload="metadata"
                          sx={{ width: '100%', height: 36 }}
                        />
                      </Box>
                    )}
                    {m.attachment_name && (
                      <Chip
                        icon={<AttachFile sx={{ fontSize: 14 }} />}
                        label={m.attachment_name}
                        size="small"
                        component={m.attachment_url ? 'a' : 'div'}
                        href={m.attachment_url || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable={!!m.attachment_url}
                        sx={{ mt: 0.5, fontSize: 11, bgcolor: 'rgba(255,255,255,0.25)', color: 'inherit' }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.25, textAlign: isMine ? 'right' : 'left', px: 0.5 }}
                  >
                    {fullTime(m.created_at)}
                    {isMine && m.read_at && (
                      <CheckCircle sx={{ fontSize: 12, ml: 0.5, color: '#00bcd4', verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                </Box>
                {!isMine && (
                  <Tooltip title="Reply">
                    <IconButton
                      className="msg-reply-btn"
                      size="small"
                      onClick={() => startReply(m)}
                      sx={{ ml: 0.5, opacity: { xs: 0.35, md: 0 }, transition: 'opacity 0.15s', color: 'text.secondary' }}
                    >
                      <Reply sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              </React.Fragment>
            );
          })}
          </>
        )}
        <div ref={bottomRef} />
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Reply preview — what the next message will quote */}
      {replyTo && (
        <Box sx={{
          mx: 2, mb: 1, px: 1.5, py: 0.75, borderRadius: 1,
          borderLeft: '3px solid #00bcd4', bgcolor: 'rgba(0,188,212,0.06)',
          display: 'flex', alignItems: 'center', gap: 1
        }}>
          <Reply sx={{ fontSize: 16, color: '#0097a7' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#0097a7' }}>
              Replying to {replyTo.sender_id === currentUserId ? 'yourself' : otherName}
            </Typography>
            <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>
              {replyTo.content || replyTo.attachment_name || 'Attachment'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
            <Close sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* Attachment preview */}
      {attachment && (
        <Box sx={{ mx: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {attachment.type?.startsWith('image/') && (
              <Box
                component="img"
                src={URL.createObjectURL(attachment)}
                alt="preview"
                sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover' }}
              />
            )}
            {attachment.type?.startsWith('video/') && (
              <Box
                component="video"
                src={URL.createObjectURL(attachment)}
                muted
                sx={{ width: 64, height: 48, borderRadius: 1, objectFit: 'cover', bgcolor: '#000' }}
              />
            )}
            <Chip
              icon={<AttachFile />}
              label={`${attachment.name} (${(attachment.size / 1024).toFixed(0)} KB)`}
              onDelete={() => { setAttachment(null); setUploadProgress(0); }}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          {sending && uploadProgress > 0 && (
            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
                <Box sx={{ width: `${uploadProgress}%`, height: '100%', bgcolor: '#00bcd4', transition: 'width 0.2s' }} />
              </Box>
              <Typography variant="caption" color="text.secondary">{uploadProgress}%</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Their local time — Slack-style. Diaspora clients and Zimbabwe
          contractors rarely share a clock; knowing it's 3 AM for the other
          person explains a slow reply better than silence does. */}
      {otherLocalTime && (
        <Box sx={{ px: 2, py: 0.75, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa' }}>
          <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            It's <strong>{otherLocalTime}</strong> for {otherName}
          </Typography>
        </Box>
      )}

      {/* First-contact templates: introduce yourself by name and business,
          say what you need to price the work, propose a next step. Shown
          automatically before a contractor's first message to a client; the
          wand button brings them back any time. */}
      {authUser?.role === 'contractor' && otherIsClient && (contractorFirstContact || showTemplates) && !recording && (
        <Box sx={{ px: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.75, borderTop: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {contractorFirstContact ? `Your first message to ${clientDisplayName || 'this client'} — start with a template:` : 'Templates:'}
            </Typography>
            {INTRO_TEMPLATES.map((tpl) => (
              <Chip key={tpl.key} size="small" variant="outlined" color="primary" label={tpl.label} onClick={() => applyTemplate(tpl)} />
            ))}
            {!contractorFirstContact && (
              <IconButton size="small" onClick={() => setShowTemplates(false)}><Close fontSize="small" /></IconButton>
            )}
          </Box>
          {missingIntroFacts(authUser).length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Templates fill in from your profile. Add {missingIntroFacts(authUser).join(', ')} under{' '}
              <Box component="span" onClick={() => navigate('/profile')} sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}>My Profile</Box>
              {' '}so clients see who they are dealing with.
            </Typography>
          )}
        </Box>
      )}
      {thinOpenerWarning && (
        <Alert
          severity="warning"
          sx={{ mx: 2, mt: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" color="inherit" onClick={() => { setThinOpenerWarning(false); setShowTemplates(true); }}>Use a template</Button>
              <Button size="small" color="inherit" onClick={() => handleSend({ force: true })}>Send anyway</Button>
            </Box>
          }
        >
          This is your first message to {clientDisplayName || 'this client'}. A short "Hi" or just a phone number rarely gets a reply — introduce yourself and say what you need.
        </Alert>
      )}

      {/* Input area */}
      <Box sx={{
        px: 2, py: 1.5, borderTop: 1, borderColor: 'divider',
        display: 'flex', alignItems: 'flex-end', gap: 1, bgcolor: '#fafafa'
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,.ppt,.pptx,.skp,.layout,.dwg,.dxf,.rvt,.rfa,.pln,.3ds,.max,.obj,.stl,.fbx,.dae,.blend,.glb,.gltf,.ifc"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {recording ? (
          /* ── Recording UI ── */
          <>
            <Tooltip title="Cancel">
              <IconButton size="small" onClick={cancelRecording} sx={{ color: 'text.secondary' }}>
                <Close />
              </IconButton>
            </Tooltip>
            <Box sx={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 1.5,
              px: 2, py: 1, borderRadius: '20px', bgcolor: '#fce4ec'
            }}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main',
                animation: 'pulse 1.2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 }
                }
              }} />
              <Typography variant="body2" color="error.main" fontWeight={500}>
                Recording {formatRecTime(recordingTime)}
              </Typography>
            </Box>
            <Tooltip title="Stop & attach">
              <IconButton
                onClick={stopRecording}
                sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
              >
                <Stop />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          /* ── Normal input UI ── */
          <>
            <Tooltip title="Attach file">
              <IconButton size="small" onClick={() => fileInputRef.current?.click()} color="default">
                <AttachFile />
              </IconButton>
            </Tooltip>
            <Tooltip title="Voice note">
              <IconButton size="small" onClick={startRecording} color="default" disabled={sending}>
                <Mic />
              </IconButton>
            </Tooltip>
            <Tooltip title="Emoji">
              <IconButton size="small" onClick={(e) => setEmojiAnchor(e.currentTarget)} color="default" disabled={sending}>
                <InsertEmoticon />
              </IconButton>
            </Tooltip>
            {authUser?.role === 'contractor' && otherIsClient && (
              <Tooltip title="Message templates">
                <IconButton size="small" onClick={() => setShowTemplates((v) => !v)} color={showTemplates ? 'primary' : 'default'} disabled={sending}>
                  <AutoAwesome />
                </IconButton>
              </Tooltip>
            )}
            <Popover
              open={Boolean(emojiAnchor)}
              anchorEl={emojiAnchor}
              onClose={() => setEmojiAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <Box sx={{ p: 1, width: 276, display: 'flex', flexWrap: 'wrap' }}>
                {EMOJI_PALETTE.map((emoji) => (
                  <Box
                    key={emoji}
                    onClick={() => insertEmoji(emoji)}
                    sx={{
                      width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, cursor: 'pointer', borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    {emoji}
                  </Box>
                ))}
              </Box>
            </Popover>
            <TextField
              fullWidth
              multiline
              maxRows={10}
              size="small"
              placeholder="Type a message…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
              inputRef={inputElRef}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px' } }}
            />
            <IconButton
              color="primary"
              onClick={() => handleSend()}
              disabled={sending || (!input.trim() && !attachment)}
              sx={{ bgcolor: '#00bcd4', color: 'white', '&:hover': { bgcolor: '#0097a7' }, '&.Mui-disabled': { bgcolor: border.default } }}
            >
              {sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const MessagingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { conversationId: routeConversationId } = useParams();
  const [searchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  // mobile: 'list' | 'thread'
  const [mobileView, setMobileView] = useState('list');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const res = await messagingService.getConversations();
      if (res.success) {
        const convs = res.data.conversations || [];
        setConversations(convs);
        return convs;
      }
    } catch (err) {
      setError('Failed to load conversations');
    }
    return [];
  }, []);

  // Opening a thread reads it, so its red badge clears instantly.
  const clearUnread = useCallback((convId) => {
    setConversations(prev => prev.map(c =>
      String(c.id) === String(convId) ? { ...c, unread_count: 0 } : c
    ));
  }, []);

  // Open a conversation by id
  const openConversation = useCallback((convId, convList) => {
    const list = convList || conversations;
    const found = list.find(c => String(c.id) === String(convId));
    if (found) {
      setSelected(found);
      clearUnread(convId);
      if (isMobile) setMobileView('thread');
    }
  }, [conversations, isMobile, clearUnread]);

  // Initial load + handle ?conversation= or ?contractorId= / ?userId= / ?projectManagerId= / ?clientId= params
  useEffect(() => {
    setLoading(true);
    loadConversations().then(async (convs) => {
      // If a conversation ID is provided, open it directly
      const paramId = searchParams.get('conversation') || routeConversationId;
      if (paramId) {
        openConversation(paramId, convs);
        navigate(`/messages?conversation=${paramId}`, { replace: true });
        return;
      }

      // If a user ID is provided (from any "Message" button), auto-create/find conversation
      const targetUserId = searchParams.get('contractorId')
        || searchParams.get('userId')
        || searchParams.get('projectManagerId')
        || searchParams.get('clientId');
      const projectId = searchParams.get('projectId') || null;

      if (targetUserId) {
        try {
          const res = await messagingService.getOrCreateConversation(targetUserId, projectId);
          const conv = res.data;
          const convId = conv?.id;
          if (convId) {
            // Try to find in loaded list (has messages) or use the response directly
            // New conversations with 0 messages won't be in the list — that's intentional.
            // We still open the thread so the user can type their first message.
            const found = convs.find(c => String(c.id) === String(convId)) || conv;
            setSelected(found);
            navigate(`/messages?conversation=${convId}`, { replace: true });
            if (isMobile) setMobileView('thread');
          }
        } catch (err) {
          console.error('[MessagingPage] Failed to create conversation with user', targetUserId, err);
        }
      }
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectConversation = (conv) => {
    setSelected(conv);
    clearUnread(conv.id);
    navigate(`/messages?conversation=${conv.id}`, { replace: true });
    if (isMobile) setMobileView('thread');
  };

  const handleDeleteConversation = async (convId) => {
    try {
      await messagingService.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (selected?.id === convId) {
        setSelected(null);
        navigate('/messages', { replace: true });
      }
    } catch (err) {
      console.error('Failed to delete conversation', err);
    }
  };

  const handleConversationCreated = async (convData) => {
    // Reload to get full conversation detail with participants
    const convs = await loadConversations();
    const found = convs.find(c => String(c.id) === String(convData?.id)) || convData;
    if (found?.id) {
      setSelected(found);
      navigate(`/messages?conversation=${found.id}`, { replace: true });
      if (isMobile) setMobileView('thread');
    }
  };

  const showList = !isMobile || mobileView === 'list';
  const showThread = !isMobile || mobileView === 'thread';

  return (
    <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', overflow: 'hidden' }}>
      {error && (
        <Alert severity="error" sx={{ position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
          {error}
        </Alert>
      )}

      {/* Left panel — conversation list */}
      {showList && (
        <Paper
          elevation={2}
          sx={{
            width: isMobile ? '100%' : 340,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            overflow: 'hidden'
          }}
        >
          <ConversationList
            conversations={conversations}
            selected={selected}
            onSelect={handleSelectConversation}
            onNew={() => setNewMsgOpen(true)}
            onDelete={handleDeleteConversation}
            loading={loading}
          />
        </Paper>
      )}

      {/* Right panel — thread */}
      {showThread && (
        <Paper
          elevation={2}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
            overflow: 'hidden',
            borderLeft: isMobile ? 'none' : `1px solid ${border.default}`
          }}
        >
          <MessageThread
            conversation={selected}
            currentUserId={user?.id}
            showBack={isMobile}
            onBack={() => { setMobileView('list'); setSelected(null); }}
          />
        </Paper>
      )}

      {/* New Message dialog */}
      <NewMessageDialog
        open={newMsgOpen}
        onClose={() => setNewMsgOpen(false)}
        onConversationCreated={handleConversationCreated}
      />
    </Box>
  );
};

export default MessagingPage;
